<template>
  <hr-form-dialog
    ref="dialog"
    size="lg"
    :title="dialogTitle"
    @hide="onDialogHide"
    @submit="onSubmit"
  >
    <div class="row q-col-gutter-md">
      <hr-list-select
        v-model="editSessionActivity.type"
        :options="selectionDisplayedOptions"
        :disable="isParent"
        :label="editSessionActivityTypeLabel"
        :rules="[val => !!val || $t('sessions.modals.sessionActivity.selectActivityValidation')]"
        hide-bottom-space
        class="col-12 col-md-4"
      />
      <client-users-selector
        v-model="sessionActivityUser"
        :client="sessionActivityClient"
        :label="$t('sessions.modals.sessionActivity.labelStaff')"
        :readonly="!hasSessionActivityClient"
        :disable-users="disableUsers"
        :disable="lockUser || isParent"
        hide-bottom-space
        class="col-12 col-md-8"
      />
      <date-input
        v-model="sessionActivityDate"
        :label="$t('sessions.modals.sessionActivity.labelStartDate')"
        outlined
        :options="startTimestampLimit"
        :disable="!canEditTimeFrame || lockDate"
        :error-message="$t('common.dateValidator')"
        hide-bottom-space
        :class="{
          'col-12 col-md-4': !hasDistance,
          'col-12 col-md-3': hasDistance
        }"
      />
      <hr-time-field
        v-model="startTimestamp"
        :label="$t('sessions.modals.sessionActivity.labelStartTime')"
        preserve-date
        :disable="!canEditTimeFrame"
        :error-message="$t('common.timeValidator')"
        :time-zone="currentIANATimezone"
        hide-bottom-space
        :class="{
          'col-6 col-md-4': !hasDistance,
          'col-6 col-md-3': hasDistance
        }"
      />
      <hr-time-field
        v-model="endTimestamp"
        :label="$t('sessions.modals.sessionActivity.labelEndTime')"
        preserve-date
        :disable="!canEditTimeFrame"
        :error-message="$t('common.timeValidator')"
        :time-zone="currentIANATimezone"
        hide-bottom-space
        :class="{
          'col-6 col-md-4': !hasDistance,
          'col-6 col-md-3': hasDistance
        }"
      />
      <hr-text-field
        v-if="hasDistance"
        v-model.number="editSessionActivity.distance"
        :label="$t('sessions.modals.sessionActivity.labelDistance')"
        :disable="isParent"
        type="number"
        hide-bottom-space
        class="col-6 col-md-3"
      />
      <service-delivery-fields
        v-if="showServiceDeliveryFields"
        :model-value="serviceDelivery"
        :places-of-service="placesOfService"
        :treatment-services="treatmentServices"
        :is-loading="isLoading"
        multiple
        @update:model-value="onUpdateServiceDelivery"
      />
    </div>
    <template #actions>
      <hr-dismiss-btn
        :label="$t('common.buttonCancel')"
        @click="hide"
      />
      <hr-confirm-btn
        type="submit"
        :label="saveBtnLabel || $t('common.buttonSave')"
        :disable="!isFormValid"
        :loading="isValidating"
        data-e2e="button-save"
      />
    </template>
  </hr-form-dialog>
</template>
<script>
import DateInput from 'components/DateInput';
import ViewSessionActivitiesDialog from 'components/SessionActivities/ViewSessionActivitiesDialog';
import ClientUsersSelector from 'components/UI/ClientUsersSelector';
import { Timestamp } from 'firebase/firestore';
import { cloneDeep, get, isEmpty, pick } from 'lodash';
import { mapState } from 'pinia';
import { useTime } from 'src/composables/time';
import {
  HrConfirmBtn, HrDismissBtn, HrFormDialog, HrListSelect,
  HrTextField, HrTimeField,
} from 'src/design-system';
import { useServiceDeliveryCatalogs } from 'src/composables/serviceDeliveryCatalogs';
import Auth from 'src/mixins/Auth';
import Interactions from 'src/mixins/Interactions';
import SessionActivitiesMixin from 'src/mixins/SessionActivitiesMixin';
import {
  canCarryServiceDelivery,
  EMPTY_SERVICE_DELIVERY,
  ServiceDeliveryFields,
  toServiceDelivery,
  toServiceDeliveryDocument,
  withTreatmentServices,
} from 'src/contexts/treatmentPlanning';
import { auth } from 'src/store/auth';

const MILLISECONDS_IN_SECOND = 1000;
const SECONDS_IN_DAY = 24 * 60 * 60;

// Billable activity types — only these capture a service delivery.

export default {
  name: 'EditSessionActivityDialog',

  components: {
    ClientUsersSelector,
    DateInput,
    HrConfirmBtn,
    HrDismissBtn,
    HrFormDialog,
    HrListSelect,
    HrTextField,
    HrTimeField,
    ServiceDeliveryFields,
  },

  mixins: [Auth, Interactions, SessionActivitiesMixin],

  props: {
    sessionActivity: {
      type: Object,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
    lockUser: {
      type: Boolean,
      default: false,
    },
    lockDate: {
      type: Boolean,
      default: false,
    },
    linkedAppointment: {
      type: Object,
      default: () => ({}),
    },
    excludedOptions: {
      type: Object,
      default: () => ({}),
    },
    saveBtnLabel: {
      type: String,
      default: '',
    },
  },

  emits: ['hide', 'ok'],

  setup() {
    const {
      getLocaleString,
      getLocaleTimeString,
      formatDate,
      adjustDate,
      getMonth,
      getDate,
      getFullYear,
      addToDate,
      subtractFromDate,
      getSeconds,
      getMilliseconds,
      currentIANATimezone,
    } = useTime();

    return {
      ...useServiceDeliveryCatalogs(),
      getLocaleString,
      getLocaleTimeString,
      formatDate,
      adjustDate,
      getDate,
      getMonth,
      getFullYear,
      addToDate,
      subtractFromDate,
      getSeconds,
      getMilliseconds,
      currentIANATimezone,
    };
  },

  data() {
    return {
      isValidating: false,
      sessionActivityDate: null,
      sessionActivityDuration: null,
      editSessionActivity: {
        user: null,
        userName: null,
        startTimestamp: null,
        endTimestamp: null,
        type: null,
        distance: null,
      },

      startTimestampLimit: (d) => {
        return d <= this.formatDate(Date.now(), 'yyyy/MM/dd');
      },

      existingDisabledOptions: [],
    };
  },

  computed: {
    ...mapState(auth, ['isParent', 'isRevenueCycleAvailable']),

    showServiceDeliveryFields() {
      return this.isRevenueCycleAvailable
        && canCarryServiceDelivery(this.editSessionActivity.type);
    },

    serviceDelivery() {
      return toServiceDelivery(this.editSessionActivity.serviceDelivery);
    },

    dialogTitle() {
      if (this.title !== null) {
        return this.title;
      }
      return this.sessionActivity.id
        ? this.$t('sessions.modals.sessionActivity.dialogTitleEdit')
        : this.$t('sessions.modals.sessionActivity.dialogTitleAdd');
    },

    isFormValid() {
      return (!!this.editSessionActivity.user)
        && (this.editSessionActivity.startTimestamp instanceof Timestamp)
        && (this.editSessionActivity.endTimestamp instanceof Timestamp)
        && (this.sessionActivityDate instanceof Timestamp);
    },

    canEditTimeFrame() {
      return (
        (this.editSessionActivity.user === this.userDetails.userId)
              && this.userHasPrivilege('sessionActivities/editOwnActivityTimeFrame')
      ) || (
        (this.editSessionActivity.user !== this.userDetails.userId)
              && this.userHasPrivilege('sessionActivities/editAssociateActivityTimeFrame')
      );
    },

    disableUsers() {
      // If user is not allowed to edit own time frame then we have to make sure that
      // user doesn't assign an activity to another user, edits the time frame and then
      // assigns the activity back to themselves.
      return this.userHasPrivilege('sessionActivities/editOwnActivityTimeFrame') ? [] : [this.userDetails.userId];
    },

    hasSessionActivityClient() {
      return this.sessionActivityClient !== null;
    },

    hasDistance() {
      return !!this.activityTypesSettings[this.editSessionActivity.type]?.hasDistance;
    },

    sessionActivityClient() {
      return get(this.editSessionActivity, 'client', null);
    },

    sessionActivityUser: {
      get() {
        return {
          user: get(this.editSessionActivity, 'user', null),
          userName: get(this.editSessionActivity, 'userName', null),
        };
      },
      set(value) {
        this.editSessionActivity.user = get(value, 'user', null);
        this.editSessionActivity.userName = get(value, 'userName', null);
      },
    },

    startTimestamp: {
      get() {
        return this.editSessionActivity.startTimestamp;
      },
      set(value) {
        if (value === null) {
          this.editSessionActivity.endTimestamp = null;
        } else if (this.sessionActivityDuration === null) {
          this.editSessionActivity.endTimestamp = null;
        } else {
          this.editSessionActivity.endTimestamp = Timestamp.fromDate(
            this.addToDate(value.toDate(), { seconds: this.sessionActivityDuration }),
          );
        }
        this.editSessionActivity.startTimestamp = value;
      },
    },

    endTimestamp: {
      get() {
        return this.editSessionActivity.endTimestamp;
      },
      set(value) {
        if (value === null) {
          this.editSessionActivity.endTimestamp = null;
          this.sessionActivityDuration = null;
          return;
        }

        this.editSessionActivity.endTimestamp = value;

        if (this.editSessionActivity.startTimestamp === null) {
          this.sessionActivityDuration = this.calculateDuration(
            this.editSessionActivity.startTimestamp,
            this.editSessionActivity.endTimestamp,
          );
          return;
        }

        const startDate = this.editSessionActivity.startTimestamp.toDate();
        const endDate = this.adjustDate(value.toDate(), {
          second: this.getSeconds(startDate),
          millisecond: this.getMilliseconds(startDate),
        });

        this.editSessionActivity.endTimestamp = Timestamp.fromDate(endDate);

        const duration = (endDate - startDate) / MILLISECONDS_IN_SECOND;
        if (duration <= 0) {
          this.editSessionActivity.endTimestamp = Timestamp.fromDate(
            this.addToDate(endDate, { days: 1 }),
          );
        } else if (duration > SECONDS_IN_DAY) {
          const daysToSubtract = Math.floor(duration / SECONDS_IN_DAY);
          this.editSessionActivity.endTimestamp = Timestamp.fromDate(
            this.subtractFromDate(endDate, { days: daysToSubtract }),
          );
        }

        this.sessionActivityDuration = this.calculateDuration(
          this.editSessionActivity.startTimestamp,
          this.editSessionActivity.endTimestamp,
        );
      },
    },

    selectionDisplayedOptions() {
      const filteredActivityTypeOptions = this.sessionActivityTypeOptions.filter(({ value }) => {
        return !this.excludedOptions[value];
      });
      return [...filteredActivityTypeOptions, ...this.existingDisabledOptions];
    },

    editSessionActivityTypeLabel() {
      return !this.editSessionActivity.type
        ? this.$t('sessions.modals.sessionActivity.selectActivityPlaceholder')
        : this.$t('sessions.modals.sessionActivity.labelActivityType');
    },
  },

  watch: {
    sessionActivity: {
      immediate: true,
      deep: true,
      handler(sessionActivity) {
        this.editSessionActivity = cloneDeep(sessionActivity);
        if (
          this.editSessionActivity?.type
          && !this.sessionActivityTypeOptions.find(({ value }) => value === this.editSessionActivity.type)
        ) {
          const { value, label, icon } = this.activityTypesSettings[this.editSessionActivity.type];
          this.existingDisabledOptions.push({ value, label, icon, disable: true });
        }
        this.sessionActivityDate = get(this.editSessionActivity, 'startTimestamp', null);
        this.sessionActivityDuration = this.calculateDuration(
          get(this.editSessionActivity, 'startTimestamp', null),
          get(this.editSessionActivity, 'endTimestamp', null),
        );
      },
    },
  },

  methods: {

    // following method is REQUIRED
    // (don't change its name --> "show")
    show() {
      this.$refs.dialog.show();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    },

    onCancelClick() {
      // we just need to hide dialog
      this.hide();
    },

    onUpdateServiceDelivery(delivery) {
      this.editSessionActivity.serviceDelivery = toServiceDeliveryDocument(delivery);
    },

    async onSubmit() {
      this.isValidating = true;

      const adjustedDate = this.sessionActivityDate.toDate();
      const day = this.getDate(adjustedDate);
      const month = this.getMonth(adjustedDate);
      const year = this.getFullYear(adjustedDate);
      this.editSessionActivity.startTimestamp = Timestamp.fromDate(
        this.adjustDate(
          this.editSessionActivity.startTimestamp.toDate(),
          { year, month, day },
        ),
      );
      this.editSessionActivity.endTimestamp = Timestamp.fromDate(
        this.addToDate(this.editSessionActivity.startTimestamp.toDate(), { seconds: this.sessionActivityDuration }),
      );

      const { distance } = this.editSessionActivity;
      const isInvalidDistance = typeof distance !== 'number';
      this.editSessionActivity.distance = isInvalidDistance ? null : Number(distance);

      try {
        if (!await this.validateActivitiesOverlapping()) {
          return;
        }

        if (!await this.validateAppointmentOverlap()) {
          return;
        }
      } catch (err) {
        this.notifyNegative(err.message);
      } finally {
        this.isValidating = false;
      }

      const changedFields = ['user', 'userName', 'startTimestamp', 'endTimestamp', 'type', 'distance'];
      const activityTypeChanged = this.editSessionActivity.type !== this.sessionActivity.type;
      const couldCarryDelivery = canCarryServiceDelivery(this.sessionActivity.type);

      // Without the billing add-on the service delivery is never shown, so it must also never be
      // written — leaving `serviceDelivery` out of changedFields entirely preserves whatever a
      // clinic captured while the add-on was on, instead of clearing data it can no longer see.
      if (this.isRevenueCycleAvailable) {
        if (this.showServiceDeliveryFields) {
          // Switching between two billable types strands services that no longer match — clear them
          // on save (they stay selected while editing so a mistaken type switch can be undone).
          // Services picked after switching in from a non-billable type, or on a new activity, are
          // kept.
          if (activityTypeChanged && couldCarryDelivery) {
            this.editSessionActivity.serviceDelivery = toServiceDeliveryDocument(
              withTreatmentServices(this.serviceDelivery ?? EMPTY_SERVICE_DELIVERY, []),
            );
          } else {
            // Write an empty delivery when nothing was edited — an empty delivery is a valid
            // billable-pending state, and distinct from the activity having none at all.
            this.editSessionActivity.serviceDelivery = toServiceDeliveryDocument(
              this.serviceDelivery ?? EMPTY_SERVICE_DELIVERY,
            );
          }
          changedFields.push('serviceDelivery');
        } else if (activityTypeChanged && couldCarryDelivery) {
          // Switched away from a billable type — drop the now-stale service delivery.
          this.editSessionActivity.serviceDelivery = null;
          changedFields.push('serviceDelivery');
        }
      }

      this.$emit(
        'ok',
        pick(this.editSessionActivity, changedFields),
      );
      this.hide();
    },

    validateActivitiesOverlapping() {
      const editStartTimestamp = this.adjustDate(this.editSessionActivity.startTimestamp, {
        second: 0,
        millisecond: 0,
      });
      const editEndTimestamp = this.adjustDate(this.editSessionActivity.endTimestamp, { second: 0, millisecond: 0 });

      return this.validateOverlappingActivities(
        editStartTimestamp,
        editEndTimestamp,
        this.editSessionActivity,
        this.sessionActivity.id,
      );
    },

    async validateAppointmentOverlap() {
      if (isEmpty(this.linkedAppointment)) {
        return true;
      }
      if (!(this.linkedAppointment.scheduledStartTimestamp instanceof Timestamp)
        || !(this.editSessionActivity.startTimestamp instanceof Timestamp)
        || !(this.editSessionActivity.endTimestamp instanceof Timestamp)
      ) {
        return true;
      }

      const scheduledStartDate = this.linkedAppointment.scheduledStartTimestamp.toDate();
      const scheduledEndDate = this.addToDate(
        scheduledStartDate,
        { seconds: this.linkedAppointment.duration || 0 },
      );
      const startTimeDiff = scheduledStartDate
        - this.editSessionActivity.startTimestamp.toDate();

      const endTimeDiff = scheduledEndDate
        - this.editSessionActivity.endTimestamp.toDate();

      if ((Math.abs(startTimeDiff) > (15 * 60 * 1000))
        || (Math.abs(endTimeDiff) > (15 * 60 * 1000))) {
        const confirmAction = new Promise((resolve) => {
          this.confirmDialog(
            this.$t('common.dialogTitleConfirm'),
            this.$t(
              'sessions.modals.sessionActivity.appointmentOverlapConfirm',
              {
                scheduledStartDate: this.getLocaleString(scheduledStartDate),
                scheduledEndDate: this.getLocaleTimeString(scheduledEndDate),
              },
            ),
            () => (resolve(true)),
            { html: true },
            () => (resolve(false)),
          );
        });
        const [confirmRes] = await Promise.all([confirmAction]);
        return confirmRes;
      }

      return true;
    }, // validateAppointmentOverlap()

    calculateDuration(startTimestamp, endTimestamp) {
      if ((startTimestamp instanceof Timestamp)
          && (endTimestamp instanceof Timestamp)) {
        return (
          endTimestamp.toDate()
            - startTimestamp.toDate()
        ) / MILLISECONDS_IN_SECOND;
      }
      return null;
    },

    openOverlappingSessionActivitiesDialog(sessionActivities) {
      this.openComponentDialog(
        ViewSessionActivitiesDialog,
        {
          sessionActivities,
          title: this.$t('sessions.modals.sessionActivity.overlappingActivities.textTitle'),
          message: this.$t('sessions.modals.sessionActivity.overlappingActivities.textMessage'),
        },
      );
    },

  },

};
</script>
